import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Metadata, CompositeKey, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IzzyChannelActionEntity, IzzyChannelTypeActionEntity, IzzyOrganizationActionEntity, IzzyMetadataEngine, ChannelActionConfiguration, AgentScopeConfiguration } from 'mj_generatedentities';
import { BCSaaSChannelEntity } from '@bluecypress/bcsaas-entities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { MJActionEntity, MJAIAgentEntity } from '@memberjunction/core-entities';

/**
 * Sub-agent display for the UI
 */
interface SubAgentDisplay {
  /** Agent ID (GUID) */
  id: string;
  /** Full agent name (e.g., "Izzy - Knowledge Gatherer") */
  fullName: string;
  /** Display name (e.g., "Knowledge Gatherer") - stripped prefix */
  displayName: string;
  /** Font Awesome icon class */
  iconClass: string;
}

/**
 * Inheritance source for an action
 */
type InheritanceSource = 'channel' | 'channelType' | 'organization';

/**
 * Unified action display that can represent actions from any level
 */
interface UnifiedActionDisplay {
  /** The action name */
  actionName: string;
  /** The IzzyAction ID */
  izzyActionId: string;
  /** Where this action comes from */
  source: InheritanceSource;
  /** The source entity (ChannelAction, ChannelTypeAction, or OrganizationAction) */
  sourceEntity: IzzyChannelActionEntity | IzzyChannelTypeActionEntity | IzzyOrganizationActionEntity | null;
  /** Status */
  status: string;
  /** Sequence */
  sequence: number;
  /** Whether credentials are configured at this level */
  hasCredentials: boolean;
  /** Configuration JSON */
  configuration: string | null;
  /** Credential ID if set */
  credentialId: string | null;
  /** Can be edited (only channel-level actions can be edited) */
  canEdit: boolean;
  /** Is being edited */
  isEditing: boolean;
  /** Edit form */
  editForm: ChannelActionEditForm;
}

/**
 * Display wrapper for ChannelAction with UI state
 */
interface ChannelActionDisplay {
  action: IzzyChannelActionEntity;
  isEditing: boolean;
  editForm: ChannelActionEditForm;
}

/**
 * Edit form for ChannelAction
 */
interface ChannelActionEditForm {
  actionId: string;
  status: 'Active' | 'Pending' | 'Revoked';
  sequence: number;
  credentialId: string | null;
  configuration: string;
  /** Agent scope mode: 'global' for all agents, 'specific' for selected agents */
  agentScopeMode: 'global' | 'specific';
  /** Selected agent IDs when mode is 'specific' */
  selectedAgentIds: string[];
}

/**
 * State interface for persisting component state
 */
interface ChannelActionsComponentState {
  selectedOrganizationId?: string;
  selectedChannelId?: string;
  statusFilter?: string;
}

/**
 * Interface for interaction events emitted by the component
 */
interface ChannelActionsInteraction {
  type: 'selectChannel' | 'createAction' | 'editAction' | 'deleteAction' | 'reorderActions';
  channelId?: string;
  actionId?: string;
}

/**
 * Interface for query params
 */
interface ChannelActionsQueryParams {
  organizationId?: string;
  channelId?: string;
}

/**
 * Channel Actions Management Component
 *
 * Manages per-channel action configurations with:
 * - Organization and Channel selection
 * - List of ChannelActions with status, sequence, action name
 * - Add/Edit/Delete ChannelAction records
 * - Credential entry (encrypted on server)
 * - Configuration JSON editor
 * - Sequence reordering
 */
@Component({
  standalone: false,
  selector: 'izzy-channel-actions',
  templateUrl: './channel-actions.component.html',
  styleUrls: ['./channel-actions.component.css']
})
export class ChannelActionsComponent implements OnInit, OnDestroy {

  // ========== Inputs/Outputs ==========
  @Input() initialState: ChannelActionsComponentState | null = null;
  @Output() stateChange = new EventEmitter<ChannelActionsComponentState>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordPKey: CompositeKey }>();
  @Output() interaction = new EventEmitter<ChannelActionsInteraction>();

  constructor(
    private location: Location,
    private route: ActivatedRoute
  ) {}

  // ========== Data ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected channels: BCSaaSChannelEntity[] = [];
  protected channelActions: ChannelActionDisplay[] = [];
  protected availableActions: MJActionEntity[] = [];

  // ========== Inheritance Display ==========
  protected unifiedActions: UnifiedActionDisplay[] = [];
  protected showInheritedActions: boolean = true;
  protected inheritedFromOrg: IzzyOrganizationActionEntity[] = [];
  protected inheritedFromChannelType: IzzyChannelTypeActionEntity[] = [];

  // ========== Selection State ==========
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;
  protected selectedChannel: BCSaaSChannelEntity | null = null;

  // ========== UI State ==========
  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected statusFilter: string = 'All';
  protected statusOptions: string[] = ['All', 'Active', 'Pending', 'Revoked'];

  // ========== Create Mode ==========
  protected isCreating: boolean = false;
  protected createForm: ChannelActionEditForm = this.getEmptyForm();

  // ========== Confirm Dialog ==========
  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  // ========== Agent Scope ==========
  /** Available sub-agents for agent scope selection */
  protected availableSubAgents: SubAgentDisplay[] = [];
  /** The parent Izzy Single Message agent ID */
  private izzySingleMessageAgentId: string | null = null;
  /** Name of the main Izzy agent */
  private readonly IZZY_MAIN_AGENT_NAME = 'Izzy - Single Message';
  /** Prefix to strip from sub-agent names for display */
  private readonly AGENT_NAME_PREFIX = 'Izzy - ';

  // ========== Cleanup ==========
  private destroy$ = new Subject<void>();
  private _pendingOrgId: string | null = null;
  private _pendingChannelId: string | null = null;

  async ngOnInit(): Promise<void> {
    // Subscribe to query param changes for deep linking
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.handleQueryParams(params);
    });

    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle query params for deep linking
   */
  private handleQueryParams(params: ChannelActionsQueryParams): void {
    if (params.organizationId) {
      this._pendingOrgId = params.organizationId;
    }
    if (params.channelId) {
      this._pendingChannelId = params.channelId;
    }

    if (!this.isLoading && this.organizations.length > 0) {
      this.applyPendingSelections();
    }
  }

  /**
   * Apply pending selections from query params
   */
  private applyPendingSelections(): void {
    if (this._pendingOrgId) {
      const org = this.organizations.find(o => o.ID === this._pendingOrgId);
      if (org && org.ID !== this.selectedOrganization?.ID) {
        this.selectOrganization(org);
      }
      this._pendingOrgId = null;
    }

    if (this._pendingChannelId) {
      const channel = this.filteredChannels.find(c => c.ID === this._pendingChannelId);
      if (channel && channel.ID !== this.selectedChannel?.ID) {
        this.selectChannel(channel);
      }
      this._pendingChannelId = null;
    }
  }

  /**
   * Update URL query params without full navigation
   */
  private updateQueryParams(): void {
    const params = new URLSearchParams();

    if (this.selectedOrganization) {
      params.set('organizationId', this.selectedOrganization.ID);
    }
    if (this.selectedChannel) {
      params.set('channelId', this.selectedChannel.ID);
    }

    const path = this.location.path().split('?')[0];
    const queryString = params.toString();
    const newUrl = queryString ? `${path}?${queryString}` : path;
    this.location.replaceState(newUrl);
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Initialize IzzyMetadataEngine
      await IzzyMetadataEngine.Instance.Config(false);

      // Load organizations from cache
      this.organizations = IzzyMetadataEngine.Instance.GetCurrentUserOrganizations();

      // Load channels from cache
      this.channels = IzzyMetadataEngine.Instance.Channels as BCSaaSChannelEntity[];

      // Load available actions and sub-agents in parallel
      await Promise.all([
        this.loadAvailableActions(),
        this.loadAvailableSubAgents()
      ]);

      // Apply pending selections or auto-select defaults
      if (this._pendingOrgId || this._pendingChannelId) {
        this.applyPendingSelections();
      } else if (!this.selectedOrganization && this.organizations.length > 0) {
        this.selectOrganization(this.organizations[0]);
      }
    } catch (error) {
      console.error('Error loading channel actions data:', error);
      this.errorMessage = 'Failed to load data. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadAvailableActions(): Promise<void> {
    try {
      const rv = new RunView();
      const result = await rv.RunView<MJActionEntity>({
        EntityName: 'MJ: Actions',
        ExtraFilter: "Status = 'Active'"
      });

      if (result.Success) {
        this.availableActions = result.Results;
      }
    } catch (error) {
      console.error('Error loading actions:', error);
    }
  }

  /**
   * Load available sub-agents for agent scope selection.
   * Finds the Izzy - Single Message agent and loads its children.
   */
  private async loadAvailableSubAgents(): Promise<void> {
    try {
      const rv = new RunView();

      // First, find the main Izzy Single Message agent
      const mainAgentResult = await rv.RunView<MJAIAgentEntity>({
        EntityName: 'MJ: AI Agents',
        ExtraFilter: `Name = '${this.IZZY_MAIN_AGENT_NAME}'`
      });

      if (!mainAgentResult.Success || mainAgentResult.Results.length === 0) {
        console.warn('Could not find Izzy - Single Message agent');
        return;
      }

      const mainAgent = mainAgentResult.Results[0];
      this.izzySingleMessageAgentId = mainAgent.ID;

      // Add the main agent first (displayed as "Izzy")
      this.availableSubAgents = [{
        id: mainAgent.ID,
        fullName: mainAgent.Name,
        displayName: 'Izzy (Orchestrator)',
        iconClass: mainAgent.IconClass || 'fa-solid fa-robot'
      }];

      // Now load all sub-agents (children of the main agent)
      const subAgentsResult = await rv.RunView<MJAIAgentEntity>({
        EntityName: 'MJ: AI Agents',
        ExtraFilter: `ParentID = '${mainAgent.ID}' AND Status = 'Active'`,
        OrderBy: 'ExecutionOrder'
      });

      if (subAgentsResult.Success) {
        for (const subAgent of subAgentsResult.Results) {
          // Strip the "Izzy - " prefix for display
          let displayName = subAgent.Name;
          if (displayName.startsWith(this.AGENT_NAME_PREFIX)) {
            displayName = displayName.substring(this.AGENT_NAME_PREFIX.length);
          }

          this.availableSubAgents.push({
            id: subAgent.ID,
            fullName: subAgent.Name,
            displayName,
            iconClass: subAgent.IconClass || 'fa-solid fa-cog'
          });
        }
      }
    } catch (error) {
      console.error('Error loading sub-agents:', error);
    }
  }

  /**
   * Toggle agent selection in edit form
   */
  protected toggleAgentSelection(form: ChannelActionEditForm, agentId: string): void {
    const index = form.selectedAgentIds.indexOf(agentId);
    if (index === -1) {
      form.selectedAgentIds.push(agentId);
    } else {
      form.selectedAgentIds.splice(index, 1);
    }
  }

  /**
   * Check if an agent is selected in the form
   */
  protected isAgentSelected(form: ChannelActionEditForm, agentId: string): boolean {
    return form.selectedAgentIds.includes(agentId);
  }

  /**
   * Get display text for agent scope
   */
  protected getAgentScopeDisplayText(configuration: string | null): string {
    if (!configuration) {
      return 'All Agents';
    }

    try {
      const config: ChannelActionConfiguration = JSON.parse(configuration);
      if (!config.agentScope || config.agentScope.mode === 'global') {
        return 'All Agents';
      }

      const agentIds = config.agentScope.agentIds || [];
      if (agentIds.length === 0) {
        return 'All Agents';
      }

      // Look up display names from availableSubAgents
      const displayNames = agentIds.map(id => {
        const agent = this.availableSubAgents.find(a => a.id === id);
        return agent?.displayName || 'Unknown';
      });

      if (displayNames.length === 1) {
        return displayNames[0];
      }
      if (displayNames.length <= 2) {
        return displayNames.join(' & ');
      }
      return `${displayNames[0]} + ${displayNames.length - 1} more`;
    } catch {
      return 'All Agents';
    }
  }

  private async loadChannelActions(): Promise<void> {
    if (!this.selectedChannel) {
      this.channelActions = [];
      this.unifiedActions = [];
      this.inheritedFromOrg = [];
      this.inheritedFromChannelType = [];
      return;
    }

    try {
      // Get ChannelActions from metadata engine (cached)
      const actions = IzzyMetadataEngine.Instance.GetChannelActions(this.selectedChannel.ID);

      this.channelActions = actions.map(action => ({
        action,
        isEditing: false,
        editForm: this.actionToForm(action)
      }));

      // Sort by sequence
      this.channelActions.sort((a, b) => a.action.Sequence - b.action.Sequence);

      // Load inherited actions
      await this.loadInheritedActions();

      // Build unified display
      this.buildUnifiedActionsView();
    } catch (error) {
      console.error('Error loading channel actions:', error);
      this.errorMessage = 'Failed to load channel actions.';
    }
  }

  /**
   * Load inherited actions from Organization and ChannelType levels
   */
  private async loadInheritedActions(): Promise<void> {
    if (!this.selectedChannel || !this.selectedOrganization) {
      this.inheritedFromOrg = [];
      this.inheritedFromChannelType = [];
      return;
    }

    try {
      // Load OrganizationActions for this organization
      const orgActions = IzzyMetadataEngine.Instance.OrganizationActions
        .filter(oa => oa.OrganizationID === this.selectedOrganization!.ID);
      this.inheritedFromOrg = orgActions;

      // Load ChannelTypeActions for this channel's type and organization
      const channelTypeId = this.selectedChannel.ChannelTypeID;
      const channelTypeActions = IzzyMetadataEngine.Instance.ChannelTypeActions
        .filter(cta => cta.ChannelTypeID === channelTypeId &&
                       cta.OrganizationID === this.selectedOrganization!.ID);
      this.inheritedFromChannelType = channelTypeActions;
    } catch (error) {
      console.error('Error loading inherited actions:', error);
    }
  }

  /**
   * Build a unified view of all actions (channel, channelType, organization)
   * This shows the effective action configuration with inheritance
   */
  private buildUnifiedActionsView(): void {
    const unified: UnifiedActionDisplay[] = [];
    const processedActionIds = new Set<string>();

    // First, add all channel-level actions (highest priority)
    for (const display of this.channelActions) {
      // ChannelAction.ActionID references __mj.Action directly
      processedActionIds.add(display.action.ActionID);

      unified.push({
        actionName: this.getActionName(display.action.ActionID),
        izzyActionId: display.action.ActionID,
        source: 'channel',
        sourceEntity: display.action,
        status: display.action.Status,
        sequence: display.action.Sequence,
        hasCredentials: !!display.action.CredentialID,
        configuration: display.action.Configuration,
        credentialId: display.action.CredentialID,
        canEdit: true,
        isEditing: display.isEditing,
        editForm: display.editForm
      });
    }

    // Then add channelType-level actions not overridden at channel level
    for (const ctAction of this.inheritedFromChannelType) {
      // ChannelTypeAction.ActionID references __mj.Action
      if (!processedActionIds.has(ctAction.ActionID)) {
        processedActionIds.add(ctAction.ActionID);

        unified.push({
          actionName: this.getActionName(ctAction.ActionID),
          izzyActionId: ctAction.ActionID,
          source: 'channelType',
          sourceEntity: ctAction,
          status: ctAction.Status,
          sequence: ctAction.Sequence,
          hasCredentials: !!ctAction.CredentialID,
          configuration: ctAction.Configuration,
          credentialId: ctAction.CredentialID,
          canEdit: false,
          isEditing: false,
          editForm: this.getEmptyForm()
        });
      }
    }

    // Finally add organization-level actions not overridden
    for (const orgAction of this.inheritedFromOrg) {
      // OrganizationAction.ActionID references __mj.Action
      if (!processedActionIds.has(orgAction.ActionID)) {
        processedActionIds.add(orgAction.ActionID);

        unified.push({
          actionName: this.getActionName(orgAction.ActionID),
          izzyActionId: orgAction.ActionID,
          source: 'organization',
          sourceEntity: orgAction,
          status: orgAction.Status,
          sequence: orgAction.Sequence,
          hasCredentials: !!orgAction.CredentialID,
          configuration: orgAction.Configuration,
          credentialId: orgAction.CredentialID,
          canEdit: false,
          isEditing: false,
          editForm: this.getEmptyForm()
        });
      }
    }

    // Sort by sequence
    unified.sort((a, b) => a.sequence - b.sequence);

    this.unifiedActions = unified;
  }

  /**
   * Get inheritance source label
   */
  getSourceLabel(source: InheritanceSource): string {
    switch (source) {
      case 'channel': return 'Channel';
      case 'channelType': return 'Channel Type';
      case 'organization': return 'Organization';
    }
  }

  /**
   * Get CSS class for inheritance source badge
   */
  getSourceClass(source: InheritanceSource): string {
    switch (source) {
      case 'channel': return 'source-channel';
      case 'channelType': return 'source-channel-type';
      case 'organization': return 'source-organization';
    }
  }

  /**
   * Toggle showing inherited actions
   */
  toggleInheritedActions(): void {
    this.showInheritedActions = !this.showInheritedActions;
  }

  /**
   * Get filtered unified actions based on showInheritedActions flag
   */
  get displayedUnifiedActions(): UnifiedActionDisplay[] {
    if (this.showInheritedActions) {
      return this.unifiedActions;
    }
    return this.unifiedActions.filter(a => a.source === 'channel');
  }

  /**
   * Copy an inherited action to the channel level for override
   */
  async copyToChannel(unified: UnifiedActionDisplay): Promise<void> {
    if (!this.selectedChannel || unified.source === 'channel') return;

    this.isSaving = true;
    try {
      const md = new Metadata();
      const newAction = await md.GetEntityObject<IzzyChannelActionEntity>('Channel Actions');

      // The izzyActionId field now directly contains the MJ Action ID
      newAction.ChannelID = this.selectedChannel.ID;
      newAction.ActionID = unified.izzyActionId;
      newAction.Status = unified.status as 'Active' | 'Pending' | 'Revoked';
      newAction.Sequence = unified.sequence;
      newAction.CredentialID = unified.credentialId;
      newAction.Configuration = unified.configuration;
      // Note: credential link is copied but points to same credential

      const saveResult = await newAction.Save();
      if (!saveResult) {
        throw new Error('Failed to save channel action');
      }

      // Refresh
      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadChannelActions();

      this.showSuccess('Action copied to channel level - you can now customize it');
    } catch (error) {
      console.error('Error copying action to channel:', error);
      this.errorMessage = 'Failed to copy action.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Organization Selection ==========

  selectOrganization(org: mjBizAppsCommonOrganizationEntity): void {
    this.selectedOrganization = org;
    this.selectedChannel = null;
    this.channelActions = [];
    this.cancelCreate();

    // Auto-select first channel for this org
    const orgChannels = this.filteredChannels;
    if (orgChannels.length > 0) {
      this.selectChannel(orgChannels[0]);
    }

    this.updateQueryParams();
  }

  onOrganizationChange(orgId: string): void {
    const org = this.organizations.find(o => o.ID === orgId);
    if (org) {
      this.selectOrganization(org);
    }
  }

  // ========== Channel Selection ==========

  get filteredChannels(): BCSaaSChannelEntity[] {
    if (!this.selectedOrganization) return [];
    return this.channels.filter(c => c.OrganizationID === this.selectedOrganization!.ID);
  }

  selectChannel(channel: BCSaaSChannelEntity): void {
    this.selectedChannel = channel;
    this.cancelCreate();
    this.loadChannelActions();
    this.updateQueryParams();

    this.interaction.emit({
      type: 'selectChannel',
      channelId: channel.ID
    });
  }

  onChannelChange(channelId: string): void {
    const channel = this.filteredChannels.find(c => c.ID === channelId);
    if (channel) {
      this.selectChannel(channel);
    }
  }

  // ========== Filtered Actions ==========

  get filteredActions(): ChannelActionDisplay[] {
    if (this.statusFilter === 'All') {
      return this.channelActions;
    }
    return this.channelActions.filter(ca => ca.action.Status === this.statusFilter);
  }

  // ========== Create Mode ==========

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
    this.cancelAllEdits();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
  }

  async saveCreate(): Promise<void> {
    if (!this.selectedChannel || !this.createForm.actionId) {
      this.errorMessage = 'Please select an action.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      const md = new Metadata();
      const newAction = await md.GetEntityObject<IzzyChannelActionEntity>('Channel Actions');

      newAction.ChannelID = this.selectedChannel.ID;
      newAction.ActionID = this.createForm.actionId;
      newAction.Status = this.createForm.status;
      newAction.Sequence = this.createForm.sequence;
      newAction.CredentialID = this.createForm.credentialId || null;
      newAction.Configuration = this.buildConfigurationFromForm(this.createForm) || null;

      const saveResult = await newAction.Save();
      if (!saveResult) {
        throw new Error('Failed to save channel action');
      }

      // Refresh the metadata cache and reload
      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadChannelActions();

      this.showSuccess('Channel action created');
      this.cancelCreate();

      this.interaction.emit({
        type: 'createAction',
        channelId: this.selectedChannel.ID,
        actionId: newAction.ID
      });
    } catch (error) {
      console.error('Error creating channel action:', error);
      this.errorMessage = 'Failed to create channel action. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Edit Mode ==========

  startEdit(display: ChannelActionDisplay): void {
    this.cancelAllEdits();
    this.cancelCreate();
    display.isEditing = true;
    display.editForm = this.actionToForm(display.action);
  }

  cancelEdit(display: ChannelActionDisplay): void {
    display.isEditing = false;
    display.editForm = this.actionToForm(display.action);
  }

  cancelAllEdits(): void {
    for (const display of this.channelActions) {
      display.isEditing = false;
      display.editForm = this.actionToForm(display.action);
    }
  }

  async saveEdit(display: ChannelActionDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      const action = display.action;

      action.ActionID = display.editForm.actionId;
      action.Status = display.editForm.status;
      action.Sequence = display.editForm.sequence;
      action.CredentialID = display.editForm.credentialId || null;
      action.Configuration = this.buildConfigurationFromForm(display.editForm) || null;

      const saveResult = await action.Save();
      if (!saveResult) {
        throw new Error('Failed to save channel action');
      }

      // Refresh cache and reload
      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadChannelActions();

      this.showSuccess('Channel action updated');

      this.interaction.emit({
        type: 'editAction',
        channelId: this.selectedChannel?.ID,
        actionId: action.ID
      });
    } catch (error) {
      console.error('Error updating channel action:', error);
      this.errorMessage = 'Failed to update channel action. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Delete ==========

  confirmDelete(display: ChannelActionDisplay): void {
    const actionName = this.getActionName(display.action.ActionID);
    this.showConfirmDialog(
      'Delete Channel Action',
      `Are you sure you want to delete the action "${actionName}" from this channel?`,
      async () => {
        await this.deleteAction(display);
      }
    );
  }

  private async deleteAction(display: ChannelActionDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      const result = await display.action.Delete();
      if (!result) {
        throw new Error('Failed to delete channel action');
      }

      // Refresh cache and reload
      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadChannelActions();

      this.showSuccess('Channel action deleted');

      this.interaction.emit({
        type: 'deleteAction',
        channelId: this.selectedChannel?.ID,
        actionId: display.action.ID
      });
    } catch (error) {
      console.error('Error deleting channel action:', error);
      this.errorMessage = 'Failed to delete channel action. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Sequence Reordering ==========

  async moveUp(display: ChannelActionDisplay): Promise<void> {
    const index = this.channelActions.indexOf(display);
    if (index <= 0) return;

    await this.swapSequence(display, this.channelActions[index - 1]);
  }

  async moveDown(display: ChannelActionDisplay): Promise<void> {
    const index = this.channelActions.indexOf(display);
    if (index < 0 || index >= this.channelActions.length - 1) return;

    await this.swapSequence(display, this.channelActions[index + 1]);
  }

  private async swapSequence(a: ChannelActionDisplay, b: ChannelActionDisplay): Promise<void> {
    this.isSaving = true;

    try {
      const tempSeq = a.action.Sequence;
      a.action.Sequence = b.action.Sequence;
      b.action.Sequence = tempSeq;

      await a.action.Save();
      await b.action.Save();

      // Refresh cache and reload
      await IzzyMetadataEngine.Instance.Config(true);
      await this.loadChannelActions();

      this.interaction.emit({
        type: 'reorderActions',
        channelId: this.selectedChannel?.ID
      });
    } catch (error) {
      console.error('Error reordering actions:', error);
      this.errorMessage = 'Failed to reorder actions.';
    } finally {
      this.isSaving = false;
    }
  }

  // ========== Helpers ==========

  private getEmptyForm(): ChannelActionEditForm {
    return {
      actionId: '',
      status: 'Active',
      sequence: this.channelActions.length > 0
        ? Math.max(...this.channelActions.map(ca => ca.action.Sequence)) + 1
        : 0,
      credentialId: null,
      configuration: '',
      agentScopeMode: 'global',
      selectedAgentIds: []
    };
  }

  private actionToForm(action: IzzyChannelActionEntity): ChannelActionEditForm {
    // Parse agent scope from configuration
    let agentScopeMode: 'global' | 'specific' = 'global';
    let selectedAgentIds: string[] = [];

    if (action.Configuration) {
      try {
        const config: ChannelActionConfiguration = JSON.parse(action.Configuration);
        if (config.agentScope) {
          agentScopeMode = config.agentScope.mode || 'global';
          selectedAgentIds = config.agentScope.agentIds || [];
        }
      } catch {
        // Invalid JSON, use defaults
      }
    }

    return {
      actionId: action.ActionID,
      status: action.Status as 'Active' | 'Pending' | 'Revoked',
      sequence: action.Sequence,
      credentialId: action.CredentialID || null,
      configuration: action.Configuration || '',
      agentScopeMode,
      selectedAgentIds
    };
  }

  /**
   * Build the Configuration JSON from the form, merging agent scope with any existing config
   */
  private buildConfigurationFromForm(form: ChannelActionEditForm): string {
    let config: ChannelActionConfiguration = {};

    // Try to preserve any existing actionConfig
    if (form.configuration) {
      try {
        const existingConfig: ChannelActionConfiguration = JSON.parse(form.configuration);
        config = { ...existingConfig };
      } catch {
        // Invalid JSON, start fresh
      }
    }

    // Set agent scope based on form
    if (form.agentScopeMode === 'specific' && form.selectedAgentIds.length > 0) {
      config.agentScope = {
        mode: 'specific',
        agentIds: form.selectedAgentIds
      };
    } else {
      // Remove agentScope if global (to keep config clean)
      delete config.agentScope;
    }

    // Return empty string if config is empty, otherwise JSON string
    if (Object.keys(config).length === 0) {
      return '';
    }
    return JSON.stringify(config);
  }

  getActionName(actionId: string): string {
    const action = this.availableActions.find(a => a.ID === actionId);
    return action?.Name || 'Unknown Action';
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Pending': return 'status-pending';
      case 'Revoked': return 'status-revoked';
      default: return '';
    }
  }

  // ========== Confirm Dialog ==========

  private showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  // ========== Utilities ==========

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  openActionRecord(display: ChannelActionDisplay): void {
    this.openRecord.emit({
      EntityName: 'Channel Actions',
      RecordPKey: CompositeKey.FromID(display.action.ID)
    });
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadChannelActionsComponent(): void {
  // Intentionally empty - prevents tree-shaking
}
